// Libraries
import React, { useContext } from 'react';
import { Node } from 'slate';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Stack,
  Typography,
  Chip
} from '@mui/material';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';

// Local
import {
  DOCUMENT_SEARCH,
  PAGINATED_SEARCH,
  KEYWORDS_BASED_ON_SEARCH,
} from '../../../Queries/nlp.js';
import { 
  ADD_PAST_SEARCH,
  SET_ZERO_NEW_PAPERS
 } from '../../../../Queries';
import { DocumentContext } from '../../../DocumentContext';
import { generateSearchStats } from '../../../Utilities/generateSearchStats';
import DocumentationTooltip from '../../../../Common/DocumentationTooltip.js';
import { addScoresToSearchResults } from '../../../Utilities/addScoresToSearchResults.js';
import { isFilterValid, filterParser, encodeFilter } from '../SearchBox/KeywordFilteringScripts';
import { notify } from '../../../../Common/notify'



const SearchMenu = ({filters, setFilters, selectorOptions, selectedOption}) => {
  const context = useContext(DocumentContext);

  const [keywordsBasedOnSearch] = useLazyQuery(KEYWORDS_BASED_ON_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const [paginatedSearch] = useLazyQuery(PAGINATED_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const [addPastSearch] = useMutation(ADD_PAST_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const [setZeroNewPapers] = useMutation(SET_ZERO_NEW_PAPERS, {
    fetchPolicy: 'network-only',
  })

  const [documentSearch] = useLazyQuery(DOCUMENT_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: ({ documentSearch }) => {
      if (documentSearch.status === 'success') {
        context.updateContext('paperIds', documentSearch.response.paper_list);
        context.updateContext(
          'rerankingScores',
          documentSearch.response.reranking_scores
        );
        context.updateContext(
          'prefetchingScores',
          documentSearch.response.prefetching_scores
        );
        context.updateContext(
          'pageCount',
          documentSearch.response.paper_list.length / 10
        );
        context.updateContext('loadingRequest', false);
        context.updateContext(
          'loadingMessage',
          generateSearchStats(documentSearch.response.search_stats)
        );

        paginatedSearch({
          variables: {
            keywords: context.keywords,
            paper_list: documentSearch.response.paper_list.slice(0, 10),
          },
          onCompleted: ({ paginatedSearch }) => {
            const updatedSearchResults = addScoresToSearchResults(
              paginatedSearch.response, // in batches of 10 papers
              documentSearch.response.reranking_scores, // scores for all papers
              documentSearch.response.prefetching_scores, // prefetching scores for all papers
              1, // current page
              10 // items per page
            );

            context.updateContext('searchResults', updatedSearchResults);
            context.updateContext('currentPage', 1);

            if (context.searchLibrary) {
              context.updateContext('searchLibrary', false);
            }
          },
        });

        keywordsBasedOnSearch({
          fetchPolicy: 'network-only',
          variables: {
            ranking_variable: context.searchTerm,
            keywords: context.keywords,
            paper_list: documentSearch.response.paper_list,
          },
          onCompleted: ({ keywordsBasedOnSearch }) => {
            context.updateContext(
              'keywordsBasedOnSearch',
              keywordsBasedOnSearch.response
            );
          },
        });
      } else {
        context.updateContext('loadingRequest', false);
        context.updateContext('loadingMessage', 'Paper retrieval failed');
      }
    },
  });

  const extractMetadata = (bookmarks) =>
    bookmarks.map((bookmark) => {
      return {
        collection: bookmark.id_collection,
        id_field: bookmark.id_field,
        id_type: bookmark.id_type,
        id_value: bookmark.id_value,
      };
    });
  
  const handleQuickSearch = () => {
    const rankingVariable = context.value.map(n => Node.string(n)).join('\n');
    let paperList;
    let rankingCollection = selectedOption;

    if (!rankingVariable && !context.keywords.length) {
      notify('Please provide search text or keywords');
      return;
    }

    context.updateContext('searchTerm', '[Contents of manuscript]');
    context.updateContext('loadingMessage', 'Retrieving papers');
    context.updateContext('loadingRequest', true);

    // If Library is selected, then only use the paper ids tied to the note
    if (selectedOption === selectorOptions[1].value) {
      paperList = extractMetadata(
        context.bookmarks.filter(bookmark =>
          context.manuscriptLibrary.some(lib => lib._id === bookmark._id)
        )
      );
      rankingCollection = '';
    }

    const cleanedFilters = filters.filter(isFilterValid);
    setFilters(cleanedFilters);

    const parsedFilters = cleanedFilters.map(filterParser).filter(f => f !== '');
    const keywordAndFilters = [...context.keywords, ...parsedFilters];

    documentSearch({
      variables: {
        ranking_variable: rankingVariable,
        keywords: keywordAndFilters,
        paper_list: paperList,
        rankingCollection: rankingCollection,
      },
    });

    addPastSearch({
      variables: {
        doc_id: context.documentId,
        searchTerm: '[Contents of manuscript]',
        keywords: context.keywords,
        filters: cleanedFilters.map(encodeFilter),
      },
    });

    setZeroNewPapers({
      variables: {
      doc_id: context.documentId,
    }});
    context.updateContext("newPapers", false)
  };


  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        padding: '5px 10px',
        borderBottom: theme =>
          theme.palette.mode === 'dark'
            ? '1px solid #333333'
            : '1px solid #d4d4d4',
        height: '35px',
        maxHeight: '35px',
        boxSizing: 'border-box',
      }}
    >
      {/* Quick-search button with its tooltip */}
      <DocumentationTooltip
        tooltipText="Discover papers relevant to your note."
        linkSuffix="help-centre/discover-the-literature#discover-papers-that-are-similar-to-a-particular-paper"
      >
        <Button
          variant="outlined"
          startIcon={
            <DocumentScannerOutlinedIcon
              sx={{
                fontSize: 'medium !important',
                display: 'inline-flex',
              }}
            />
          }
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            height: '24px',       // force consistent height
            minHeight: '24px',
            px: 1,                // tighten horizontal padding
          }}
          onClick={handleQuickSearch}
        >
          <Typography
            variant="inherit"
            sx={{
              fontSize: '10px',
              lineHeight: '24px', // center text vertically
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Quick search
          </Typography>
        </Button>
      </DocumentationTooltip>
  
      {context.newPapers && (
        <DocumentationTooltip tooltipText="Click quick search to see the new papers">
          <Chip
            label="New papers"
            size="small"
            color="primary"
            sx={{
              fontSize: '10px',
              height: '20px',
              minHeight: '20px',
            }}
          />
        </DocumentationTooltip>
      )}
    </Stack>
  );
  
};

export default SearchMenu;
